import React, { useState } from "react"
import { FormProvider, get, useForm } from "react-hook-form"

import Button from "legacy/components/shared/Button"
import { Element, Text } from "legacy/components/shared/base"
import Input from "legacy/components/shared/Input"
interface OwnProps {
  bgInput?: string
  buttonText?: string
  formConfirmationText: string
  formErrorText: string
  formHiddenFields?: { [key: string]: string }
  formInputPlaceholder?: string
  formInputType: string
  formName: string
}

export type ActionCallFormProps = OwnProps

const ActionCallForm: React.FC<ActionCallFormProps> = ({
  bgInput,
  buttonText,
  formConfirmationText,
  formErrorText,
  formHiddenFields,
  formInputType = "phone",
  formInputPlaceholder = formInputType,
  formName = "form-call-to-action",
}): React.ReactElement | null => {
  const [formState, setFormState] = useState({
    isSubmitted: false,
  })

  interface FormValues {
    formInputType: string
    "form-name": string
  }

  const form = useForm<FormValues>()
  const { errors } = form.formState
  const error = get(errors, formInputType)

  const encode = (data: FormValues): string => {
    return Object.entries(data)
      .map(
        ([key, value]) =>
          encodeURIComponent(key) + "=" + encodeURIComponent(value)
      )
      .join("&")
  }

  const onSubmit = (data: FormValues): void => {
    const formAdditionalRequestBody =
      formHiddenFields && `&${encode(formHiddenFields)}`

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `form-name=${formName}&${formInputType}=${data[formInputType]}${formAdditionalRequestBody}`,
    })
      .then(() => {
        setFormState({
          isSubmitted: true,
        })
      })
      .catch(error => {
        console.error("Error - Location Not Found - Form: ", error)
        // Rerender form inputs on error so user can try to resubmit again
        setFormState({
          isSubmitted: false,
        })
      })
  }

  return (
    <Element display="grid" gridTemplateColumns="repeat(8,1fr)" mt="35">
      <FormProvider {...form}>
        {!formState.isSubmitted && (
          <Element
            as="form"
            data-netlify="true"
            gridColumnEnd="8"
            gridColumnStart="2"
            method="POST"
            onSubmit={form.handleSubmit(onSubmit)}
            name={formName}
          >
            <Input
              bg={bgInput}
              border="1SolidNavy"
              color="navy"
              fontFamily="martel"
              fontSize={["12", "16"]}
              fontWeight="semibold"
              letterSpacing="0.09em"
              placeholder={formInputPlaceholder}
              pb={["10", "20"]}
              pt={["12", "22"]}
              px={["20", "30"]}
              required
              textTransform="uppercase"
              type={formInputType}
              variant="block"
              width="100%"
              {...form.register(formInputType, { formErrorText })}
            />
            {formHiddenFields &&
              Object.entries(formHiddenFields).map(([key, value]) => (
                <input key={key} name={key} type="hidden" value={value} />
              ))}
            <Element mx="auto">
              <Button
                fontSize={["16", "text.button"]}
                name="submit"
                mt="35"
                type="submit"
              >
                {buttonText}
              </Button>
              {error && error.message && (
                <Text
                  fontSize={["16", "text.error"]}
                  m={["20px auto 0"]}
                  variant="error"
                >
                  {error.message}
                </Text>
              )}
            </Element>
          </Element>
        )}
        {formState.isSubmitted && (
          <Text gridColumnEnd="8" gridColumnStart="2">
            {formConfirmationText}
          </Text>
        )}
      </FormProvider>
    </Element>
  )
}

export default ActionCallForm
